import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft,
  Heart,
  MapPin,
  Shield,
  Sparkles,
  Copy,
  Check,
  IndianRupee,
  ExternalLink,
  Github,
  Mail,
} from "lucide-react";

const UPI_ID = "lakshmivid@okhdfcbank";
const UPI_NAME = "Loocator for India - App Funding";
const NOTE = "Support Loocator for India";

// Google Pay's UPI deep-link parser is strict RFC-3986 — it does NOT treat
// "+" as a space (that's only an HTML-form convention). `URLSearchParams`
// encodes spaces as "+", which silently breaks the `pn`/`tn` fields for GPay:
// the payee's bank-verified name then fails to resolve and the Pay button
// stays disabled, even though the UPI ID itself is valid. Build the query
// string manually with encodeURIComponent (-> %20) instead.
const buildUpiQuery = (fields) =>
  Object.entries(fields)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join("&");

const buildUpiUrl = (amount) => {
  const query = buildUpiQuery({
    pa: UPI_ID,
    pn: UPI_NAME,
    cu: "INR",
    tn: NOTE,
    ...(amount && Number(amount) > 0 ? { am: String(amount) } : {}),
  });
  return `upi://pay?${query}`;
};

const QR_API = (data) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=320x320&margin=10&data=${encodeURIComponent(data)}`;

const QUICK_AMOUNTS = [49, 99, 199, 499, 999];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCount = (n) => {
  if (n == null) return "—";
  if (n >= 1000) return `${Math.floor(n / 100) / 10}k+`;
  return `${n}+`;
};

const APPS = [
  { id: "gpay",    label: "Google Pay", scheme: "gpay://upi/pay",   color: "#4285F4" },
  { id: "phonepe", label: "PhonePe",    scheme: "phonepe://pay",    color: "#5F259F" },
  { id: "paytm",   label: "Paytm",      scheme: "paytmmp://pay",    color: "#00BAF2" },
  { id: "bhim",    label: "BHIM",       scheme: "upi://pay",        color: "#1E5B3F" },
];

export default function AboutPage() {
  const navigate = useNavigate();
  const [amount, setAmount] = useState("");
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState(null);

  const upiUrl = buildUpiUrl(amount);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/stats/public`)
      .then((r) => { if (!cancelled) setStats(r.data); })
      .catch(() => { if (!cancelled) setStats({ restrooms: 0, users: 0, states_covered: 0 }); });
    return () => { cancelled = true; };
  }, []);

  const copyUpi = async () => {
    try {
      await navigator.clipboard.writeText(UPI_ID);
      setCopied(true);
      toast.success("UPI ID copied to clipboard");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copy failed");
    }
  };

  const appUrl = (scheme) => {
    const query = buildUpiQuery({
      pa: UPI_ID,
      pn: UPI_NAME,
      cu: "INR",
      tn: NOTE,
      ...(amount && Number(amount) > 0 ? { am: String(amount) } : {}),
    });
    return `${scheme}?${query}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-rose-50 pb-12" data-testid="about-page">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur border-b border-slate-100 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center">
          <button
            onClick={() => navigate("/")}
            className="flex items-center text-slate-500 hover:text-slate-700"
            data-testid="about-back-btn"
          >
            <ArrowLeft size={20} className="mr-2" /> Back
          </button>
          <h1 className="text-lg font-bold ml-4">About</h1>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 pt-8 space-y-10">
        {/* Founder profile */}
        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="text-center"
        >
          <div className="mx-auto w-28 h-28 rounded-full bg-gradient-to-br from-sky-500 to-rose-500 flex items-center justify-center shadow-lg ring-4 ring-white" data-testid="founder-avatar">
            <span className="text-4xl font-bold text-white tracking-wide">SS</span>
          </div>
          <h2 className="mt-4 text-3xl sm:text-4xl font-extrabold text-slate-900" data-testid="founder-name">
            Srividhya S
          </h2>
          <p className="mt-1 text-sm font-semibold tracking-wide text-sky-700 uppercase">
            Founder &amp; Creator of Loocator for India
          </p>

          <div className="mt-6 mx-auto max-w-xl text-slate-600 leading-relaxed text-base">
            <p>
              Hi, I&apos;m Srividhya. Loocator for India started from a simple, painful question I
              kept asking on the road: <em>“Where is a clean, safe restroom I can
              actually use right now?”</em> The answer should never be a guess —
              especially for women, kids, and travelers.
            </p>
            <p className="mt-3">
              Loocator for India maps real, verified options near you — public toilets, malls,
              petrol pumps, toll plazas and highway services — backed by community
              ratings on cleanliness and safety. It runs on open data
              (OpenStreetMap), uses no tracking ads, and is built to be free to use.
            </p>
            <p className="mt-3 italic text-slate-500">
              Built with care so no one has to settle for unsafe.
            </p>
          </div>

          <div className="mt-5 flex flex-wrap items-center justify-center gap-3 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1 bg-white border border-slate-200 px-3 py-1 rounded-full">
              <MapPin size={12} className="text-sky-600" /> Made in India
            </span>
            <span className="inline-flex items-center gap-1 bg-white border border-slate-200 px-3 py-1 rounded-full">
              <Shield size={12} className="text-emerald-600" /> Women-first by design
            </span>
            <span className="inline-flex items-center gap-1 bg-white border border-slate-200 px-3 py-1 rounded-full">
              <Sparkles size={12} className="text-amber-500" /> Powered by OpenStreetMap
            </span>
          </div>
        </motion.section>

        {/* Mission strip */}
        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.4 }}
          className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 grid sm:grid-cols-3 gap-4 text-center"
          data-testid="mission-stats"
        >
          <div data-testid="stat-restrooms">
            <p className="text-3xl font-extrabold text-sky-700">
              {stats ? formatCount(stats.restrooms) : "…"}
            </p>
            <p className="text-xs text-slate-500 mt-1">Mapped restrooms</p>
          </div>
          <div data-testid="stat-states">
            <p className="text-3xl font-extrabold text-rose-600">
              {stats ? (stats.states_covered || 0) : "…"}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {(stats?.states_covered || 0) === 1 ? "State covered" : "States & UTs covered"}
            </p>
          </div>
          <div data-testid="stat-free">
            <p className="text-3xl font-extrabold text-emerald-600">100%</p>
            <p className="text-xs text-slate-500 mt-1">Free &amp; ad-free</p>
          </div>
        </motion.section>

        {/* Support / UPI funding */}
        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.4 }}
          className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden"
          data-testid="support-section"
        >
          <div className="bg-gradient-to-r from-rose-500 to-pink-600 text-white p-5 flex items-center gap-3">
            <Heart size={22} className="fill-white" />
            <div>
              <h3 className="font-bold text-lg">Support Loocator for India</h3>
              <p className="text-sm text-white/85">
                Hosting, map tiles, and maintenance keep this app running. Any amount helps.
              </p>
            </div>
          </div>

          <div className="p-5 space-y-5">
            {/* UPI ID + copy */}
            <div className="flex items-center gap-2 bg-slate-50 rounded-xl border border-slate-200 p-3">
              <IndianRupee size={20} className="text-rose-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-slate-500">UPI ID</p>
                <p className="font-mono font-semibold text-slate-900 truncate" data-testid="upi-id">{UPI_ID}</p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={copyUpi}
                className="rounded-full"
                data-testid="copy-upi-btn"
              >
                {copied ? <Check size={14} className="mr-1" /> : <Copy size={14} className="mr-1" />}
                {copied ? "Copied" : "Copy"}
              </Button>
            </div>

            {/* Amount + quick-picks */}
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-2">SUGGESTED AMOUNT (OPTIONAL)</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {QUICK_AMOUNTS.map((a) => {
                  const active = String(a) === amount;
                  return (
                    <button
                      key={a}
                      onClick={() => setAmount(String(a))}
                      className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all ${
                        active
                          ? "bg-rose-600 text-white border-rose-600"
                          : "bg-white text-slate-700 border border-slate-200 hover:border-rose-400"
                      }`}
                      data-testid={`amount-chip-${a}`}
                    >
                      ₹{a}
                    </button>
                  );
                })}
                <button
                  onClick={() => setAmount("")}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-all ${
                    !amount
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"
                  }`}
                  data-testid="amount-chip-custom"
                >
                  Any amount
                </button>
              </div>
              <div className="relative">
                <IndianRupee size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  type="number"
                  min="1"
                  inputMode="numeric"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^\d]/g, ""))}
                  placeholder="Enter custom amount"
                  className="pl-9 rounded-xl"
                  data-testid="custom-amount-input"
                />
              </div>
            </div>

            {/* QR code */}
            <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 flex flex-col sm:flex-row items-center gap-4">
              <img
                src={QR_API(upiUrl)}
                alt={`UPI QR code for ${UPI_ID}`}
                className="w-44 h-44 rounded-lg bg-white p-2 shadow-sm"
                data-testid="upi-qr-image"
              />
              <div className="flex-1 text-center sm:text-left">
                <p className="font-semibold text-slate-900">Scan to pay</p>
                <p className="text-sm text-slate-500 mt-1">
                  Open any UPI app (GPay, PhonePe, Paytm, BHIM) and scan this QR.
                  {amount ? <> The amount <b>₹{amount}</b> will be pre-filled.</> : null}
                </p>
                <p className="text-xs text-slate-400 mt-2">Payee: {UPI_NAME}</p>
              </div>
            </div>

            {/* App deep-links */}
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-2">PAY DIRECTLY VIA</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {APPS.map((app) => (
                  <a
                    key={app.id}
                    href={appUrl(app.scheme)}
                    className="flex items-center justify-center gap-2 text-white font-semibold rounded-xl py-3 px-2 shadow-sm hover:opacity-90 active:scale-95 transition-all text-sm"
                    style={{ backgroundColor: app.color }}
                    data-testid={`pay-${app.id}-btn`}
                  >
                    {app.label}
                  </a>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-2 text-center">
                Buttons open the app on mobile. On desktop, use the QR code above.
              </p>
            </div>
          </div>
        </motion.section>

        {/* Contact */}
        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.4 }}
          className="text-center text-sm text-slate-500"
        >
          <p className="mb-3">Have a story, a bug, or a city to add?</p>
          <div className="flex items-center justify-center gap-3">
            <a
              href="mailto:help@safeloocator.in?subject=Loocator%20for%20India%20Feedback"
              className="inline-flex items-center gap-1 bg-white border border-slate-200 px-4 py-2 rounded-full hover:border-slate-400"
              data-testid="contact-email-btn"
            >
              <Mail size={14} /> Get in touch
            </a>
            <a
              href="https://www.openstreetmap.org/copyright"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 bg-white border border-slate-200 px-4 py-2 rounded-full hover:border-slate-400"
            >
              <ExternalLink size={14} /> Data &amp; credits
            </a>
          </div>
          <p className="mt-6 text-xs">
            © {new Date().getFullYear()} Loocator for India · Built with ♥ for safer streets
          </p>
        </motion.section>
      </div>
    </div>
  );
}
